// Authorizations — prior auths and referrals, with the visits-remaining counter
// and the alerts strip. Instance C's registry page.
//
// Atlas keeps the book. Nothing on this page requests an authorization from a
// payer — that is a phone call, a fax or a portal login — so there is no "submit"
// anywhere, by design.
//
// Props from the registry: selectedPractice, features, isAdmin, readOnly,
// onPageChange. App.js has already decided the feature is on before rendering
// this, so the gate is not re-checked here; `isAdmin && !readOnly` decides
// whether the write controls exist, matching the backend, where the writes are
// admin-gated.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import useCapabilities from "../../hooks/useCapabilities";
import ActionList from "./ActionList";
import AlertsStrip from "./AlertsStrip";
import AuthorizationModal from "./AuthorizationModal";
import { linesFrom, requestStateFrom, unattributedFrom } from "./api";
import { LinesUsage } from "./DisciplineLines";
import FeedStrip from "./FeedStrip";
import OutcomeModal from "./OutcomeModal";
import {
  API_URL,
  SourceBadge,
  StatusChip,
  STATUS_FILTERS,
  STATUS_LABELS,
  UsageBar,
  errorFrom,
  formatWindow,
} from "./AuthBits";
import "./AuthorizationsPage.css";

const TYPE_LABELS = { prior_auth: "Prior auth", referral: "Referral" };

export default function AuthorizationsPage({
  selectedPractice,
  isAdmin,
  readOnly,
}) {
  const practiceId = selectedPractice?.id;
  const canWrite = Boolean(isAdmin) && !readOnly;
  // The request cycle (Mark requested, Record answer) is biller-and-above on the
  // server, like working the queue — wider than add/edit, which is admin.
  const canRequest = !readOnly;

  // NOT to decide whether this page exists — App.js already did that and renders
  // <FeatureOff/> otherwise, which is A's contract. This reads `capabilities` to
  // vary WITHIN the page: a practice whose source supplies auths has its rows
  // maintained by a sync, and a biller needs to know that before wondering why
  // an edit did not stick. The hook is a module-level cache, so this reuses the
  // answer App.js already fetched rather than issuing a second request.
  const { capabilities, source, features } = useCapabilities(practiceId);
  const sourceFeedsAuths = (capabilities || []).includes("auths");
  // The auth-advisor sprint's surfaces — the action list and discipline lines —
  // vary within the page on `features.auth_advisor`, which A computes from
  // `authorizations`. Off (or not yet known) renders the page exactly as before.
  const advisorOn = Boolean(features && features.auth_advisor);
  // Bumped after every write so the action list re-asks the advisor: an edit to
  // a window or a line can add or clear a flag.
  const [advisorGen, setAdvisorGen] = useState(0);

  const [rows, setRows] = useState([]);
  const [alerts, setAlerts] = useState({
    alerts: [],
    counts: null,
    truncated: false,
  });
  // Both start TRUE. The honest first-paint state is "we have not asked yet",
  // and with AlertsStrip now treating a null `counts` as "we do not know", a
  // false here would flash "could not check" before the first request is even
  // made.
  const [loading, setLoading] = useState(true);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [payerFilter, setPayerFilter] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // {} for a new one, a row to edit
  const [retiring, setRetiring] = useState(null);
  const [answering, setAnswering] = useState(null);
  const [payers, setPayers] = useState([]);
  const [alertsFailed, setAlertsFailed] = useState(false);
  const [payersFailed, setPayersFailed] = useState(false);
  const [listFailed, setListFailed] = useState(false);

  // STALENESS: the practice AND a per-loader sequence. Both are needed, and the
  // first fix here got it wrong by using only the practice.
  //
  // An effect's cleanup flag cancels only the load that effect started. It
  // cannot cancel the reload `afterWrite` fires, which closes over the practice
  // that was current when the biller hit Save — so save, switch practice, and
  // that reload lands last and wins, persistently.
  //
  // But keying on the practice ALONE reintroduces the same bug within one
  // practice, which is what the effect flag had covered: two saves in a row
  // leave two reloads in flight, both pass a practice check, and if the first
  // resolves last the table shows the older generation and STAYS there — a
  // used/remaining counter one write out of date, under the right practice
  // name, with nothing to indicate it and no self-correction. The header's
  // "Add authorization" stays live while the table is hidden during a reload,
  // so that is reachable through the page's own flow, not only by navigating.
  //
  // So: a response is applied only if it still answers the question on screen
  // (same practice) AND no later request of its kind has been issued since.
  //
  // The ref write below happens during render, which React calls impure. It is
  // safe here because a practice change is a discrete click and App.js uses no
  // startTransition or Suspense — but it replaced the codebase's effect-flag
  // pattern, so it is worth knowing that is the assumption it rests on.
  const currentPracticeRef = useRef(practiceId);
  currentPracticeRef.current = practiceId;
  const loadSeq = useRef(0);
  const alertsSeq = useRef(0);
  const payersSeq = useRef(0);

  // Two different questions, and conflating them latched the page in
  // "Loading…" for good:
  //   stale() — should this RESPONSE be applied? No if the practice moved on,
  //             and no if a later request of the same kind has been issued.
  //   owns()  — does this loader still own the spinner it turned on? That must
  //             ignore the practice, or a load whose practice changed
  //             mid-flight returns early and the flag it set is never cleared
  //             by anyone.
  const guard = (seqRef, askedFor) => {
    const mine = ++seqRef.current;
    return {
      stale: () =>
        currentPracticeRef.current !== askedFor || seqRef.current !== mine,
      owns: () => seqRef.current === mine,
    };
  };

  const load = useCallback(async () => {
    if (!practiceId) return;
    const askedFor = practiceId;
    // `afterWrite`'s reload closes over the practice that was current at Save.
    // If the biller has switched since, this is answering a question nobody is
    // asking — bail BEFORE claiming a sequence or turning on a spinner, rather
    // than starting work only to discard it.
    if (currentPracticeRef.current !== askedFor) return;
    const { stale, owns } = guard(loadSeq, askedFor);
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${askedFor}/authorizations`,
        { credentials: "include" },
      );
      const payload = await response.json().catch(() => null);
      if (stale()) return;
      // A 200 whose body did not parse, or parsed to something without the key
      // we asked for, is a FAILURE — not an empty practice. Reporting "no
      // authorizations on file" because a proxy returned an HTML error page is
      // the same confident-wrong-answer this page keeps having to be stopped
      // from giving. `PatientAuths` already gets this right.
      if (!response.ok || !payload || !Array.isArray(payload.authorizations)) {
        setMessage({
          kind: "error",
          text: errorFrom(response, payload, "Could not load authorizations"),
        });
        setRows([]);
        setListFailed(true);
        return;
      }
      setRows(payload.authorizations);
      setListFailed(false);
      // Clear a stale failure banner: the load that just succeeded is the
      // answer to the one that did not.
      setMessage((m) => (m && m.kind === "error" ? null : m));
    } catch (err) {
      if (stale()) return;
      setMessage({
        kind: "error",
        text: "Could not reach the server to load authorizations.",
      });
      setRows([]);
      setListFailed(true);
    } finally {
      if (owns()) setLoading(false);
    }
  }, [practiceId]);

  const loadAlerts = useCallback(async () => {
    if (!practiceId) return;
    const askedFor = practiceId;
    // `afterWrite`'s reload closes over the practice that was current at Save.
    // If the biller has switched since, this is answering a question nobody is
    // asking — bail BEFORE claiming a sequence or turning on a spinner, rather
    // than starting work only to discard it.
    if (currentPracticeRef.current !== askedFor) return;
    const { stale, owns } = guard(alertsSeq, askedFor);
    setAlertsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${askedFor}/authorizations/alerts`,
        { credentials: "include" },
      );
      const payload = await response.json().catch(() => null);
      if (stale()) return;
      // The table survives an alerts failure — but the strip says so rather
      // than rendering its all-clear. An unparseable or shape-less 200 counts
      // as a failure here for the same reason as above: `counts` going null
      // makes every kind read zero, which IS the all-clear.
      // Every kind must be a real number. `!payload.counts` catches null and
      // undefined and misses `{}` — which is truthy, makes every kind read
      // undefined, and renders the green all-clear. That is the same confident
      // wrong answer through the one shape that still slipped.
      const countsOk =
        payload &&
        payload.counts &&
        ["exhausted", "expiring", "missing"].every((k) =>
          Number.isFinite(payload.counts[k]),
        );
      if (!response.ok || !countsOk) {
        setAlerts({ alerts: [], counts: null, truncated: false });
        setAlertsFailed(true);
        return;
      }
      setAlerts({
        alerts: payload.alerts || [],
        counts: payload.counts,
        truncated: Boolean(payload.truncated),
      });
      setAlertsFailed(false);
    } catch (err) {
      if (stale()) return;
      setAlerts({ alerts: [], counts: null, truncated: false });
      setAlertsFailed(true);
    } finally {
      if (owns()) setAlertsLoading(false);
    }
  }, [practiceId]);

  const loadPayers = useCallback(async () => {
    if (!practiceId) return;
    const askedFor = practiceId;
    // `afterWrite`'s reload closes over the practice that was current at Save.
    // If the biller has switched since, this is answering a question nobody is
    // asking — bail BEFORE claiming a sequence or turning on a spinner, rather
    // than starting work only to discard it.
    if (currentPracticeRef.current !== askedFor) return;
    const { stale, owns } = guard(payersSeq, askedFor);
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${askedFor}/authorizations/payers`,
        { credentials: "include" },
      );
      const payload = await response.json().catch(() => null);
      if (stale()) return;
      if (!response.ok || !payload || !Array.isArray(payload.payers)) {
        setPayers([]);
        setPayersFailed(true);
        return;
      }
      setPayers(payload.payers);
      setPayersFailed(false);
    } catch (err) {
      if (stale()) return;
      setPayers([]);
      setPayersFailed(true);
    }
  }, [practiceId]);

  // Clear everything practice-scoped the moment the practice changes, so no
  // frame is ever painted with the previous practice's rows under the new
  // practice's name. The filters go too: a payer filter carried over from
  // practice A silently hides every row in practice B.
  useEffect(() => {
    setRows([]);
    setAlerts({ alerts: [], counts: null, truncated: false });
    setAlertsFailed(false);
    setPayers([]);
    setPayersFailed(false);
    setMessage(null);
    setStatusFilter("");
    setPayerFilter("");
    setSearch("");
    setEditing(null);
    setRetiring(null);
    setAnswering(null);
    setLoading(true);
    setAlertsLoading(true);
  }, [practiceId]);

  useEffect(() => {
    load();
    loadAlerts();
    loadPayers();
  }, [load, loadAlerts, loadPayers]);

  const afterWrite = useCallback(
    (text) => {
      // The loaders bail when the practice moved on; this has to as well, or
      // "Authorization SAVED-1 added." renders under the OTHER practice's name
      // after a save-then-switch — a success message about work done somewhere
      // the biller is no longer looking.
      if (currentPracticeRef.current !== practiceId) return;
      setMessage({ kind: "success", text });
      load();
      loadAlerts();
      setAdvisorGen((n) => n + 1);
    },
    [practiceId, load, loadAlerts],
  );

  // The FILTER lists only payers that actually appear on a row, so it never
  // offers a choice that yields nothing. The MODAL uses the full `payers` list
  // from the API, because a new authorization may well be the practice's first
  // for that payer.
  const payerNamesInUse = useMemo(() => {
    const names = new Set();
    rows.forEach((r) => {
      if (r.payer_name) names.add(r.payer_name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  // Filtering is client-side on purpose: the list is one practice's
  // authorizations, the backend already sent every row, and a round trip per
  // keystroke would make the search feel worse without making it more correct.
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter && r.computed_status !== statusFilter) return false;
      if (payerFilter && r.payer_name !== payerFilter) return false;
      if (!needle) return true;
      return (
        (r.patient_name || "").toLowerCase().includes(needle) ||
        (r.auth_number || "").toLowerCase().includes(needle) ||
        (r.payer_name || "").toLowerCase().includes(needle)
      );
    });
  }, [rows, statusFilter, payerFilter, search]);

  // Straight from a `missing` alert into a create, carrying what the alert
  // already knows. The effective window defaults to a year from the date that
  // needs covering, starting on that date — a payer's approval never starts
  // after the visit it is meant to cover.
  const createFor = useCallback((subject) => {
    if (!subject) {
      setEditing({});
      return;
    }
    // The window defaults to one year from the date that needs covering. The
    // end date is NOT left open: `status_for` can never call an open-ended
    // authorization expired or expiring, so a prefilled row would suppress that
    // patient's missing-auth alert forever. A real prior auth always has an end
    // date, and a wrong-but-visible one is corrected in the form; an invisible
    // one is not.
    // Built from the STRING PARTS, never via Date + toISOString(). `new
    // Date("2026-09-09T00:00:00")` parses as local and toISOString() converts
    // to UTC, so east of Greenwich the slice lands a day early. Correct for
    // every US timezone and therefore not a live bug for Atlas today, which is
    // exactly why it would survive until it was reused somewhere it mattered.
    // Date.UTC in, toISOString out — both ends UTC, so there is no local-time
    // round trip to shift the day. Plain string surgery on the year looked
    // safer and is not: a 29 February date of service becomes "2029-02-29",
    // which does not exist, and the server rejects it with a date error the
    // biller never typed. Date.UTC rolls it to 1 March instead.
    const from = subject.date_of_service || "";
    let to = "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(from)) {
      const [y, m, d] = from.split("-").map(Number);
      to = new Date(Date.UTC(y + 1, m - 1, d)).toISOString().slice(0, 10);
    }
    setEditing({
      _prefill: true,
      patient_id: subject.patient_id ?? "",
      patient_name: subject.patient_name || "",
      practice_payer_id: subject.practice_payer_id ?? "",
      payer_name: subject.payer_name || "",
      effective_from: from,
      effective_to: to,
    });
  }, []);

  const [retireError, setRetireError] = useState(null);
  const [retireBusy, setRetireBusy] = useState(false);
  // A ref for the same reason as the modal's: the state is read through a
  // render-time closure, so synchronous repeats all see `false`. Retire is
  // idempotent so there is no data harm, but three POSTs is still three.
  const retireBusyRef = useRef(false);

  const doRetire = useCallback(
    async (row) => {
      // The error goes INSIDE the dialog, not to the page banner — the banner
      // renders behind the modal's scrim, so a failed retire left the dialog
      // open, empty and silent with the reason hidden under 50% black. And a
      // busy flag, because without one a triple-click fired three POSTs.
      if (retireBusyRef.current) return;
      retireBusyRef.current = true;
      setRetireBusy(true);
      setRetireError(null);
      try {
        const response = await fetch(
          `${API_URL}/api/practices/${practiceId}/authorizations/${row.id}/retire`,
          { method: "POST", credentials: "include" },
        );
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          setRetireError(
            errorFrom(response, payload, "Could not retire the authorization"),
          );
          return;
        }
        setRetiring(null);
        afterWrite(`Authorization ${row.auth_number} is retired.`);
      } catch (err) {
        setRetireError("Could not reach the server to retire it.");
      } finally {
        retireBusyRef.current = false;
        setRetireBusy(false);
      }
    },
    [practiceId, afterWrite],
  );

  if (!practiceId) {
    return (
      <div className="auth-page">
        <h1 className="auth-title">Authorizations</h1>
        <p className="auth-empty">
          Select a practice to see its authorizations.
        </p>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-header">
        <div>
          <h1 className="auth-title">Authorizations</h1>
          <p className="auth-subtitle">
            Prior authorizations and referrals for {selectedPractice.name}.
            Atlas keeps the record; requesting an authorization is still a call
            or a portal.
          </p>
        </div>
        {canWrite && (
          <button
            type="button"
            className="auth-btn auth-btn-primary"
            onClick={() => setEditing({})}
          >
            Add authorization
          </button>
        )}
      </div>

      {message && (
        <div className={`auth-message auth-message-${message.kind}`}>
          <span>{message.text}</span>
          <button
            type="button"
            className="auth-message-close"
            onClick={() => setMessage(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {sourceFeedsAuths && (
        <div className="auth-fed-note">
          Authorizations for this practice are fed by{' '}
          <strong>{(source && source.label) || (source && source.key) || 'its source system'}</strong>.
          Synced rows are read-only here — change them at the source, or the next sync will
          overwrite the edit. Anything added by hand stays editable.
        </div>
      )}

      {advisorOn && (
        <ActionList
          // Keyed by practice so an open Mark requested dialog, and the list it
          // belongs to, never survive a switch onto another practice's page.
          key={`advisor-${practiceId}`}
          practiceId={practiceId}
          generation={advisorGen}
          canRequest={canRequest}
          onCreate={canWrite ? createFor : null}
          onChanged={afterWrite}
          onStale={load}
        />
      )}

      <AlertsStrip
        // Keyed by practice so the strip's expanded kind does not survive a
        // switch — otherwise "missing" stays open over the next practice's
        // alerts, which are a different set entirely.
        key={practiceId}
        alerts={alerts.alerts}
        counts={alerts.counts}
        truncated={alerts.truncated}
        loading={alertsLoading}
        error={alertsFailed}
        onCreate={canWrite ? createFor : null}
      />

      <FeedStrip
        // Keyed by practice so an open dry-run result or the skill dialog never
        // survives a switch onto another practice's page.
        key={`feed-${practiceId}`}
        practiceId={practiceId}
        canWrite={canWrite}
        onImported={() => {
          load();
          loadAlerts();
          setAdvisorGen((n) => n + 1);
        }}
      />

      <div className="auth-filters">
        <input
          type="search"
          className="auth-input auth-search"
          placeholder="Search patient, payer or number"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="auth-input"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>
        <select
          className="auth-input"
          value={payerFilter}
          onChange={(e) => setPayerFilter(e.target.value)}
          aria-label="Filter by payer"
        >
          <option value="">All payers</option>
          {payerNamesInUse.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <span className="auth-count">
          {visible.length === rows.length
            ? `${rows.length} authorization${rows.length === 1 ? "" : "s"}`
            : `${visible.length} of ${rows.length}`}
        </span>
      </div>

      {loading ? (
        <p className="auth-empty">Loading…</p>
      ) : listFailed ? (
        // NOT "no authorizations on file". The banner above does say the load
        // failed, but a banner is dismissable and this is not — so the empty
        // state has to consult the failure itself, or dismissing the banner
        // leaves only the false claim on screen. Same rule AlertsStrip follows.
        <p className="auth-empty">
          The authorization list could not be loaded, so this is showing nothing
          rather than nothing being there. Reload to try again.
        </p>
      ) : rows.length === 0 ? (
        <p className="auth-empty">
          No authorizations on file.
          {canWrite
            ? " Add one when a payer approves a course of treatment."
            : ""}
        </p>
      ) : visible.length === 0 ? (
        <p className="auth-empty">No authorization matches those filters.</p>
      ) : (
        <table className="auth-table">
          <thead>
            <tr>
              <th>Patient</th>
              <th>Payer</th>
              <th>Type</th>
              <th>Number</th>
              <th>Window</th>
              <th>Used</th>
              <th>Status</th>
              {canWrite && <th className="auth-actions-col">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const fed = Boolean(row.source_system);
              // Waiting on the payer is the advisor's state, not the source's, so
              // a fed row can still have its answer recorded here.
              const pending =
                advisorOn && requestStateFrom(row).outcome === "pending";
              return (
                <tr key={row.id}>
                  <td>
                    {row.patient_name || (
                      <span className="auth-muted">Unmatched</span>
                    )}
                    <SourceBadge sourceSystem={row.source_system} />
                  </td>
                  <td>
                    {row.payer_name || <span className="auth-muted">—</span>}
                  </td>
                  <td>{TYPE_LABELS[row.auth_type] || row.auth_type}</td>
                  <td className="auth-number">{row.auth_number}</td>
                  <td className="auth-window">
                    {formatWindow(row.effective_from, row.effective_to)}
                  </td>
                  <td className="auth-usage-cell">
                    <UsageBar
                      used={row.used}
                      remaining={row.remaining}
                      authorizedUnits={row.authorized_units}
                      status={row.computed_status}
                    />
                    {advisorOn && (
                      <LinesUsage
                        lines={linesFrom(row)}
                        unattributedVisits={unattributedFrom(row)}
                      />
                    )}
                  </td>
                  <td>
                    <StatusChip status={row.computed_status} />
                    {pending && (
                      <span className="auth-chip auth-chip-waiting">
                        Waiting on payer
                      </span>
                    )}
                  </td>
                  {canWrite && (
                    <td className="auth-actions-col">
                      {pending && (
                        <button
                          type="button"
                          className="auth-btn auth-btn-link"
                          onClick={() =>
                            setAnswering({
                              authId: row.id,
                              authNumber: row.auth_number,
                              patientName: row.patient_name,
                              authority: row.payer_name,
                              ref: requestStateFrom(row).ref,
                            })
                          }
                        >
                          Record answer
                        </button>
                      )}
                      {fed ? (
                        <span
                          className="auth-muted"
                          title="This row is maintained by its source system"
                        >
                          read-only
                        </span>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="auth-btn auth-btn-link"
                            onClick={() => setEditing(row)}
                          >
                            Edit
                          </button>
                          {row.computed_status !== "revoked" && (
                            <button
                              type="button"
                              className="auth-btn auth-btn-link auth-btn-danger"
                              onClick={() => {
                              setRetireError(null);
                              setRetiring(row);
                            }}
                            >
                              Retire
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {editing && (
        <AuthorizationModal
          practiceId={practiceId}
          authorization={editing.id ? editing : editing._prefill ? editing : null}
          payers={payers}
          payersFailed={payersFailed}
          showLines={advisorOn}
          onClose={() => setEditing(null)}
          onSaved={(text) => {
            setEditing(null);
            afterWrite(text);
          }}
        />
      )}

      {answering && (
        <OutcomeModal
          practiceId={practiceId}
          subject={answering}
          onClose={() => setAnswering(null)}
          onStale={() => {
            load();
            setAdvisorGen((n) => n + 1);
          }}
          onDone={(text) => {
            setAnswering(null);
            afterWrite(text);
          }}
        />
      )}

      {retiring && (
        // The × and the scrim have to be closed off too, not just Cancel and the
        // confirm. The error now renders INSIDE this dialog, so closing it
        // mid-POST throws the only thing that would have reported the failure —
        // `setRetireError` writes to state nothing is left to render it, and a
        // failed retire becomes completely silent. Before the error moved in
        // here it at least reached the (dimmed) page banner.
        <div
          className="auth-modal-overlay"
          onClick={retireBusy ? undefined : () => setRetiring(null)}
        >
          <div
            className="auth-modal auth-modal-small"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="auth-modal-header">
              <h3>Retire this authorization?</h3>
              <button
                type="button"
                className="auth-modal-close"
                onClick={() => setRetiring(null)}
                disabled={retireBusy}
              >
                ×
              </button>
            </div>
            <div className="auth-modal-body">
              {retireError && (
                <div className="auth-message auth-message-error auth-message-inline">
                  {retireError}
                </div>
              )}
              <p>
                {retiring.auth_number} for{" "}
                {retiring.patient_name || "this patient"} will be marked
                revoked.
              </p>
              <p className="auth-modal-note">
                Nothing is deleted. The authorization stays on file as the
                record of what the payer approved, and any claim that cites it
                keeps its reference.
              </p>
            </div>
            <div className="auth-modal-footer">
              <button
                type="button"
                className="auth-btn"
                onClick={() => setRetiring(null)}
                disabled={retireBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="auth-btn auth-btn-primary auth-btn-danger-solid"
                onClick={() => doRetire(retiring)}
                disabled={retireBusy}
              >
                {retireBusy ? "Retiring…" : "Retire it"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
